import { useCallback, useMemo, useState, type ReactNode } from 'react';
import type { Membresia, Operaciones, PreferenciasMenu } from '../api/operaciones';
import type { NombreIcono } from '../componentes/iconos';
import { useAvisoBreve } from '../componentes/avisoBreve';
import { EstadoCargando, EstadoError, EstadoNoContratado, EstadoSinPermiso, EstadoVacia, Hueco } from '../componentes/EstadoPagina';
import { rolesDe } from '../logica/acceso';
import { useDatosNegocio } from '../logica/datosNegocio';
import { estadoDeError } from '../logica/estados';
import { franjasVisibles, type Franja } from '../logica/franjas';
import { anidadasDe, destinoDe, iniciales, type EntradaMenu, type Menu } from '../logica/navegacion';
import { PANTALLAS, PANTALLAS_CON_APARTADOS, PANTALLAS_SUELTAS, RUTAS_SUELTAS, TARJETAS_PROPIAS } from '../pantallas/registro';
import type { PropsPantalla } from '../pantallas/tipos';
import { PermisosContexto } from '../permisos/permisos';
import { BLOQUE_DE_EJEMPLO } from '../simulacion/ejemplos';
import { Anidadas } from './Anidadas';
import { elementosCampana, textoSinAvisos, type ElementoCampana } from './avisosCampana';
import { dibujoDe } from './iconosMenu';
import { MarcoMovil } from './MarcoMovil';
import { MarcoOrdenador } from './MarcoOrdenador';
import { inicialesDe, lineaDe } from './negocio';
import { useEsMovil } from './tamano';
import { ContextoCabecera, SIN_HERRAMIENTAS, type Herramientas } from './cabecera';
import { ProveedorDosPasos } from './VentanaDosPasos';

/**
 * Panel de un negocio: datos (`useDatosNegocio`) y lo que comparten los dos marcos (0.1): menú, selector de negocio,
 * campana, perfil, franja y contenido. Móvil < 1024 px con barra inferior y «Más»; ordenador ≥ 1024 px con menú lateral
 * de 248 y cabecera en curva. Las pantallas de cada sección llegan con su módulo: aquí, su hueco o el estado de 0.4.
 */
export interface Vista {
    movil: boolean;
    membresias: readonly Membresia[];
    negocioId: string;
    nombreNegocio: string;
    lineaNegocio: string;
    inicialesNegocio: string;
    persona: { nombre: string; email: string; iniciales: string; rol: string };
    menu: Menu;
    /** Entrada del menú (o del perfil) que se ve; null mientras carga. */
    actual: EntradaMenu | null;
    titulo: string;
    /** Línea bajo el título, si la pantalla la sube (`useCabecera`, con la palabra del negocio, G214). */
    subtitulo: string | null;
    soloLectura: boolean;
    franja: Franja | null;
    campana: { elementos: ElementoCampana[]; vacio: string; sinLeer: number; abrir: () => void; leerTodos: () => void; tocar: (e: ElementoCampana) => void; accion: (e: ElementoCampana) => void; verTodos: (() => void) | null };
    ir: (entrada: EntradaMenu) => void;
    cambiarNegocio: (id: string) => void;
    cerrarSesion: () => void;
    accionFranja: (f: Franja) => void;
    cerrarFranja: (f: Franja) => void;
    /**
     * «Personalizar menú» (G129): null si la persona no puede (con un acceso de plataforma). Lee lo guardado para
     * «Restablecer» y guarda cada cambio; el menú vuelve del servidor con él aplicado.
     */
    personalizar: { leer: () => Promise<PreferenciasMenu>; guardar: (cambios: PreferenciasMenu) => Promise<void> } | null;
    /** «?» y botón principal de la pantalla abierta (componentes.md §1b), que la pantalla sube con `useCabecera`. */
    herramientas: Herramientas;
    contenido: ReactNode;
    /**
     * Marco de portal (componentes.md §17): el mismo marco para una zona sin negocio, como el portal de partners (1.23).
     * Sin campana, con el aviso de la zona en la ceja del menú (ordenador) y en la barra de arriba (móvil, negra con el
     * logo), y en el móvil todas las entradas en la barra de abajo, sin «Más».
     */
    portal?: { ceja: string; icono: NombreIcono };
}

interface Props {
    ops: Operaciones;
    negocio: string;
    ruta: string;
    busqueda: URLSearchParams;
    navegar: (ruta: string, reemplazar?: boolean) => void;
    simulacion: boolean;
    onCambiarNegocio: (id: string) => void;
    onCerrarSesion: () => void;
    onSesionCaducada: () => void;
    onFaltanDosPasos: () => void;
    /**
     * Tras «Salir» de un acceso de plataforma abierto por la persona de Reservaria (`viewer = platform`), la vuelta al
     * backoffice (1.20): `true` si ha vuelto. Si no (o sin ella), se vuelve a pedir el panel.
     */
    onSalirDelAcceso?: () => boolean;
}

export function PanelNegocio({ ops, negocio, ruta, busqueda, navegar, simulacion, onCambiarNegocio, onCerrarSesion, onSesionCaducada, onFaltanDosPasos, onSalirDelAcceso }: Props) {
    const movil = useEsMovil();
    const avisar = useAvisoBreve();
    const onError = useCallback((texto: string) => {
        avisar({ tipo: 'error', texto });
    }, [avisar]);
    const d = useDatosNegocio({ ops, negocio, ruta, busqueda, navegar, simulacion, onSesionCaducada, onFaltanDosPasos, onError, rutasPropias: RUTAS_SUELTAS });
    // Franjas cerradas (solo las amarillas): se recuerdan en el servidor, `dismissed_notices` (G129).
    const { arriba, enLaCampana } = franjasVisibles(d.franjas, d.franjasCerradas);
    const membresia = d.membresia;
    const nombreNegocio = d.nombreNegocio;
    const [saliendo, setSaliendo] = useState(false);
    const [herramientas, setHerramientas] = useState<Herramientas | null>(null);
    const rol = membresia === undefined ? '' : rolesDe(membresia);
    const actual = d.pantalla.tipo === 'desconocida' ? null : d.pantalla.entrada;
    const suelta = d.pantalla.tipo === 'desconocida' ? (PANTALLAS_SUELTAS[ruta] ?? null) : null;
    const rutaDe = (clave: string) => d.menu.perfil.find((e) => e.clave === clave)?.ruta ?? d.menu.entradas.find((e) => e.clave === clave)?.ruta ?? null;
    const rutaAvisos = rutaDe('avisos');

    // «Salir» cierra el acceso de plataforma que manda el servidor (G137) y vuelve a pedir el panel.
    const salirDelAcceso = () => {
        if (d.acceso === null || saliendo) {
            return;
        }
        setSaliendo(true);
        const alBackoffice = d.acceso.viewer === 'platform' ? onSalirDelAcceso : undefined;
        ops.closePlatformAccess(d.acceso.id)
            .catch(() => undefined)
            .finally(() => {
                setSaliendo(false);
                if (alBackoffice?.() !== true) {
                    d.reintentar();
                }
            });
    };

    const accionFranja = (f: Franja) => {
        if (f.destino === 'salir') {
            salirDelAcceso();
            return;
        }
        const destino =
            f.destino === 'plan'
                ? d.menu.rutaPlan
                : f.destino === 'consumo'
                  ? d.menu.rutaPlan === null ? null : `${d.menu.rutaPlan}?apartado=consumo`
                  : f.destino === 'actividad'
                    ? rutaDe('actividad')
                    : null;
        if (destino !== null) {
            navegar(destino);
        }
    };

    const destinos = d.datos?.navegacion.link_targets;
    const elementos = useMemo(() => elementosCampana(enLaCampana, d.campana.avisos, destinos), [enLaCampana, d.campana.avisos, destinos]);

    const ir = (entrada: EntradaMenu) => {
        const destino = destinoDe(d.menu, entrada);
        if (destino !== null) {
            navegar(destino);
        }
    };

    const titulo = actual?.etiqueta ?? suelta?.titulo ?? '';
    const propsPantalla: PropsPantalla | null =
        d.datos === null
            ? null
            : {
                  ops,
                  negocio,
                  yo: d.datos.yo,
                  membresia,
                  nombreNegocio,
                  soloLectura: d.soloLectura,
                  movil,
                  ruta,
                  busqueda,
                  navegar,
                  recargar: d.reintentar,
                  onSesionCaducada,
                  accesoPlataforma: d.acceso !== null,
                  destinos: d.datos.navegacion.link_targets,
                  recargarAvisos: d.campana.recargar,
              };
    const contenido = (() => {
        if (d.datos === null && d.error === null) {
            return <EstadoCargando seccion={titulo === '' ? 'el panel' : titulo} />;
        }
        if (d.error !== null) {
            return <EstadoError seccion={titulo === '' ? 'el panel' : titulo} codigoSoporte={estadoDeError(d.error).codigoSoporte} onReintentar={d.reintentar} movil={movil} />;
        }
        if (actual === null) {
            return suelta !== null && propsPantalla !== null ? <suelta.Componente {...propsPantalla} /> : null;
        }
        const alInicio = d.rutaInicial === null ? null : () => {
            navegar(d.rutaInicial ?? '/');
        };
        switch (d.estadoForzado) {
            case 'cargando':
                return <EstadoCargando seccion={titulo} />;
            case 'vacia':
                return <EstadoVacia seccion={titulo} />;
            case 'error':
                return <EstadoError seccion={titulo} codigoSoporte="7F3A-91C2" onReintentar={d.reintentar} movil={movil} />;
            case 'sin-permiso':
                return <EstadoSinPermiso seccion={titulo} rol={rol} negocio={nombreNegocio} onInicio={alInicio} movil={movil} />;
            case 'no-contratado':
                // Pendiente P0-7: nombre y ventajas del bloque saldrán del catálogo; hasta entonces, el ejemplo del prototipo.
                return (
                    <EstadoNoContratado
                        bloque={BLOQUE_DE_EJEMPLO.nombre}
                        incluye={BLOQUE_DE_EJEMPLO.incluye}
                        propietario={membresia?.is_owner === true}
                        onAnadir={d.menu.rutaPlan === null ? null : () => {
                            navegar(d.menu.rutaPlan ?? '/');
                        }}
                        onInicio={alInicio}
                        movil={movil}
                    />
                );
            default: {
                const apartado = d.pantalla.tipo === 'menu' ? (d.pantalla.apartado ?? null) : null;
                const dentro = anidadasDe(d.menu, actual.clave);
                const Pantalla = apartado === null ? PANTALLAS[actual.clave] : undefined;
                if (Pantalla !== undefined && propsPantalla !== null) {
                    // Una pantalla con su barra de apartados (G141) pinta ella los `section`; aquí solo quedan las tarjetas.
                    const propias = TARJETAS_PROPIAS[actual.clave];
                    const debajo = dentro.filter((e) => !(PANTALLAS_CON_APARTADOS.has(actual.clave) && e.anidadaComo === 'section') && !(e.anidadaComo === 'card' && !e.noContratada && propias?.has(e.clave) === true));
                    return (
                        <>
                            <Pantalla {...propsPantalla} anidadas={dentro} />
                            {debajo.length > 0 && <Anidadas madre={actual} anidadas={debajo} abierta={null} onIr={ir} />}
                        </>
                    );
                }
                return (
                    <>
                        <Hueco seccion={apartado?.etiqueta ?? titulo} icono={dibujoDe((apartado ?? actual).icono)} />
                        {dentro.length > 0 && <Anidadas madre={actual} anidadas={dentro} abierta={apartado} onIr={ir} />}
                    </>
                );
            }
        }
    })();

    const vista: Vista = {
        movil,
        membresias: d.membresias,
        negocioId: negocio,
        nombreNegocio,
        lineaNegocio: membresia === undefined ? '' : lineaDe(membresia),
        inicialesNegocio: membresia === undefined ? iniciales(nombreNegocio) : inicialesDe(membresia),
        persona: {
            nombre: d.datos?.yo.account.display_name ?? '',
            email: d.datos?.yo.account.email ?? '',
            iniciales: iniciales(d.datos?.yo.account.display_name ?? ''),
            rol: rol === '' ? '' : `${rol} en ${nombreNegocio}`,
        },
        menu: d.menu,
        actual,
        // El de la pantalla abierta si lo sube con `useCabecera` (G779: «Mi agenda» del Profesional).
        titulo: herramientas?.titulo ?? titulo,
        subtitulo: herramientas?.subtitulo ?? null,
        soloLectura: d.soloLectura,
        franja: arriba,
        campana: {
            elementos,
            vacio: textoSinAvisos(false),
            sinLeer: d.campana.sinLeer,
            abrir: d.campana.recargar,
            leerTodos: d.campana.leerTodos,
            tocar: (e) => {
                if (e.aviso !== null) {
                    d.campana.abrir(e.aviso);
                }
            },
            accion: (e) => {
                if (e.franja !== null) {
                    accionFranja(e.franja);
                }
                // El enlace de un aviso (G298, G340): se da por leído y se va a su destino.
                if (e.aviso !== null && e.ruta !== null) {
                    d.campana.abrir(e.aviso);
                    navegar(e.ruta);
                }
            },
            // «Ver todos los avisos» lleva a la sección Avisos (Entregas) cuando la API la dé en el menú.
            verTodos: rutaAvisos === null ? null : () => {
                navegar(rutaAvisos);
            },
        },
        ir,
        cambiarNegocio: (id) => {
            if (id !== negocio) {
                onCambiarNegocio(id);
            }
        },
        cerrarSesion: onCerrarSesion,
        accionFranja,
        cerrarFranja: (f) => {
            if (f.clave !== null) {
                d.cerrarFranja(f.clave);
            }
        },
        personalizar: d.personalizable ? { leer: d.leerPreferencias, guardar: d.guardarPreferencias } : null,
        herramientas: herramientas ?? SIN_HERRAMIENTAS,
        contenido: <ContextoCabecera.Provider value={setHerramientas}>{contenido}</ContextoCabecera.Provider>,
    };

    return (
        <PermisosContexto.Provider value={d.permisos}>
            <ProveedorDosPasos movil={movil}>{movil ? <MarcoMovil v={vista} /> : <MarcoOrdenador v={vista} />}</ProveedorDosPasos>
        </PermisosContexto.Provider>
    );
}
